import React, { useState, useEffect, useRef } from 'react'; // Importation de useRef
import { useTranslation } from 'react-i18next';
import i18n from './i18n'; // Importation directe de l'instance i18n
import {
  Globe,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  Settings,
  Leaf,
  Package,
  HardHat,
  Truck,
  Users,
  Pill,
  Sparkles,
  FlaskConical,
  UtensilsCrossed,
  Clock,
  Facebook,
  Linkedin,
  Instagram,
  Send,
  Search
} from 'lucide-react';
// Assurez-vous que votre fichier CSS (App.css) est importé pour les styles globaux si nécessaire.
import './App.css'; 

// --- Composant MegaMenuItem ---
// Ce composant gère l'affichage d'un élément de menu dans le méga-menu
const MegaMenuItem = ({ title, links }) => (
  <div className="mb-6">
    <h3 className="font-semibold text-xs text-gray-800 mb-3">
      {title}
    </h3>
    <ul className="space-y-2">
      {(Array.isArray(links) ? links : []).map((link, index) => (
        <li key={index}>
          <a href={link.href} className="text-xs text-gray-600 hover:text-[#4B73B7] transition-colors duration-200 flex items-center">
            {link.text}
            {link.icon && <ChevronRight className="ml-1 w-4 h-4 text-gray-400" />}
          </a>
        </li>
      ))}
    </ul>
  </div>
);


// --- Composant MegaMenu ---
// Ce composant représente le contenu du méga-menu pour la section "Nos Services"
const MegaMenu = ({ activeMenu, setActiveMenu, t }) => {
  // Le MegaMenu est spécifiquement pour 'ourServices' dans cette configuration
  if (activeMenu !== 'ourServices') return null;

  return (
    <div
      // Le méga-menu s'étend sur une large portion de la nav et est centré
      className="absolute top-full w-[70%] left-1/2 -translate-x-1/2 bg-white shadow-xl rounded-b-lg border-t border-gray-100 py-8 z-40" // Largeur réduite à 70%
      onMouseEnter={() => setActiveMenu('ourServices')} // Garde le menu ouvert au survol du contenu
      onMouseLeave={() => { setActiveMenu(null); }} // Ferme le flyout en quittant le méga-menu
    >
      <div className="container mx-auto px-4 grid grid-cols-2 gap-x-8"> {/* max-w-7xl pour aligner le contenu */}
        {/* Simplifié pour lier directement aux sections de services */}
        <div className="px-4 py-2">
          <h3 className="font-semibold text-xs text-gray-800 mb-3">
            {t('ourServices.title')}
          </h3>
          <ul className="space-y-2">
            <li><a href="#service-conception" className="block px-4 py-2 text-xs text-gray-600 hover:bg-gray-100">{t('ourServices.section1.title')}</a></li>
            <li><a href="#service-moules" className="block px-4 py-2 text-xs text-gray-600 hover:bg-gray-100">{t('ourServices.section2.title')}</a></li>
            <li><a href="#service-injection" className="block px-4 py-2 text-xs text-gray-600 hover:bg-gray-100">{t('ourServices.section3.title')}</a></li>
            <li><a href="#service-contenants" className="block px-4 py-2 text-xs text-gray-600 hover:bg-gray-100">{t('ourServices.section4.title')}</a></li>
            <li><a href="#service-logistique" className="block px-4 py-2 text-xs text-gray-600 hover:bg-gray-100">{t('ourServices.section5.title')}</a></li>
            <li><a href="#service-client" className="block px-4 py-2 text-xs text-gray-600 hover:bg-gray-100">{t('ourServices.section6.title')}</a></li>
          </ul>
        </div>
      </div>
    </div>
  );
};


// --- Composant Header ---
const Header = () => {
  const { t, i18n } = useTranslation();
  const [activeMenu, setActiveMenu] = useState(null); // Gère le méga-menu actif (ex: 'ourServices', 'ourServices.product' pour mobile)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // Gère l'ouverture du menu mobile complet
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false); // État pour le dropdown de langue
  const [copiedMessage, setCopiedMessage] = useState(null); // État pour le message de copie (téléphone, email, localisation)

  const navRef = useRef(null); // Référence pour le composant de navigation

  const changeLanguage = (lng) => {
    console.log(`Tentative de changement de langue vers : ${lng}`);
    i18n.changeLanguage(lng);
    setIsLanguageDropdownOpen(false); // Ferme le dropdown après sélection
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Ferme tous les sous-menus ouverts quand le menu mobile principal est fermé
    if (isMobileMenuOpen) {
      setActiveMenu(null); 
    }
  };

  // Gestion du survol pour le menu desktop
  const handleMouseEnterDesktop = (menuName) => {
    setActiveMenu(menuName);
  };

  const handleMouseLeaveDesktop = () => {
    // Cette fonction ne sera plus utilisée pour fermer le menu "Nos Services"
    // car le MegaMenu gère sa propre fermeture.
    // Elle pourrait être utilisée pour d'autres menus si nécessaire.
    setActiveMenu(null);
  };

  // Fonction générique pour copier du texte
  const copyToClipboard = (text, messageType) => {
    try {
      const tempInput = document.createElement('textarea');
      tempInput.value = text;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      setCopiedMessage(messageType); // Définit le type de message copié
      setTimeout(() => setCopiedMessage(null), 2000); // Le message disparaît après 2 secondes
    } catch (err) {
      console.error(`Échec de la copie de ${messageType}:`, err);
    }
  };

  // Fonctions spécifiques pour chaque élément copiable
  const handleCopyPhone = (event) => {
    event.preventDefault();
    copyToClipboard(t('contact_us.phone'), 'phone');
  };

  const handleCopyEmail = (event) => {
    event.preventDefault();
    copyToClipboard(t('contact_us.email'), 'email');
  };

  const handleCopyLocation = (event) => {
    event.preventDefault();
    copyToClipboard(t('contact_us.address'), 'location');
  };


  // Effet pour surveiller les changements de langue de i18n
  useEffect(() => {
    console.log(`Langue actuelle de i18n: ${i18n.language}`);
  }, [i18n.language]);

  // Effet pour gérer les clics en dehors du menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifie si le menu mobile est ouvert
      if (isMobileMenuOpen) {
        // Vérifie si le clic est en dehors de l'élément nav ET non sur le bouton burger lui-même
        const burgerIcon = document.getElementById('mobile-menu-button');
        if (navRef.current && !navRef.current.contains(event.target) && (!burgerIcon || !burgerIcon.contains(event.target))) {
          setIsMobileMenuOpen(false);
          setActiveMenu(null); // Ferme également les sous-menus actifs
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]); // Dépendance sur isMobileMenuOpen pour réattacher l'écouteur lorsque l'état du menu change


  return (
    <header className="fixed w-full z-50 mt-6 left-0 flex justify-center px-4">
      {/* Conteneur principal du header avec coins arrondis */}
      {/* La nav s'étend sur toute la largeur de l'écran, son contenu est centré par max-w-7xl mx-auto */}
      <nav ref={navRef} className="w-full max-w-7xl mx-auto bg-white text-gray-800 shadow-xl rounded-2xl md:rounded-3xl bg-opacity-80 relative">
        {/* Top section - Contenu centré */}
        {/* Réduit le padding vertical et masque email/location sur mobile */}
        <div className="flex justify-between items-center py-1 px-6 text-xs border-b border-gray-200"  dir="ltr">
          {/* Section gauche: Téléphone, Email, Localisation */}
          <div className="flex items-center space-x-4"> {/* Utilisation de space-x-4 pour l'espacement */}
            <div className="relative flex items-center">
              <Phone className="w-4 h-4 mr-1 text-gray-500" />
              <a
                href={`tel:${t('contact_us.phone')}`}
                onClick={handleCopyPhone}
                className="text-gray-600 underline hover:text-[#4B73B7] transition-colors duration-200 inline-block" dir="ltr"
              >
                {t('contact_us.phone')}
              </a>
              {copiedMessage === 'phone' && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap animate-fade-in-out">
                  Copié !
                </span>
              )}
            </div>

            {/* Masqué sur mobile, affiché sur md et plus */}
            <div className="relative hidden md:flex items-center">
              <Mail className="w-4 h-4 mr-1 text-gray-500" />
              <a
                href={`mailto:${t('contact_us.email')}`}
                onClick={handleCopyEmail}
                className="text-gray-600 underline hover:text-[#4B73B7] transition-colors duration-200"
              >
                {t('contact_us.email')}
              </a>
              {copiedMessage === 'email' && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap animate-fade-in-out">
                  Copié !
                </span>
              )}
            </div>

            {/* Masqué sur mobile, affiché sur md et plus */}
            <div className="relative hidden md:flex items-center">
              <MapPin className="w-4 h-4 mr-1 text-gray-500" />
              <a
                href="https://maps.app.goo.gl/your-location-link" // Remplacez par un lien Google Maps réel si disponible
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCopyLocation}
                className="text-gray-600 underline hover:text-[#4B73B7] transition-colors duration-200"
              >
                {t('contact_us.address')}
              </a>
              {copiedMessage === 'location' && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap animate-fade-in-out">
                  Copié !
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-4"> {/* Utilisation de space-x-4 pour l'espacement */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="text-gray-600 hover:text-gray-900 flex items-center transition-colors duration-200"
              >
                <Globe className="w-4 h-4 mr-1" /> {i18n.language.toUpperCase()} <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-24 bg-white shadow-lg rounded-md z-[60]">
                  <button
                    onClick={() => changeLanguage('fr')}
                    className={`block w-full text-left px-4 py-2 text-xs hover:bg-gray-100 ${i18n.language === 'fr' ? 'font-bold text-[#4B73B7]' : 'text-gray-700'}`}
                  >
                    FR
                  </button>
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`block w-full text-left px-4 py-2 text-xs hover:bg-gray-100 ${i18n.language === 'en' ? 'font-bold text-[#4B73B7]' : 'text-gray-700'}`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => changeLanguage('ar')}
                    className={`block w-full text-left px-4 py-2 text-xs hover:bg-gray-100 ${i18n.language === 'ar' ? 'font-bold text-[#4B73B7]' : 'text-gray-700'}`}
                  >
                    AR
                  </button>
                </div>
              )}
            </div>
            {/* <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors duration-200 flex items-center">
              <Search className="w-4 h-4 mr-1" />
            </a> */}
            
                                {/* Drapeau Algérie */}
                    <div className="flex items-center ml-2">
                      <img 
                        src="https://flagcdn.com/w40/dz.png" 
                        alt="Algérie" 
                        className="w-6 h-auto rounded-[2px] shadow-sm border border-gray-200"
                        title="Preform Inject - Algérie"
                      />
                    </div>

          </div>
        </div>

        {/* Réduit le padding vertical de la barre principale */}
        <div className="flex justify-between items-center py-2 px-6" dir="ltr"> {/* Réduit py-3 à py-2 */}
          <div className="flex-shrink-0">
            <a href="#">
              <img
                src="/images/LOGO-INJECT-BLEU_fonds_transparent.png"
                alt="Preform-Inject Logo"
                className="h-8 md:h-10 w-auto" // Réduit la hauteur du logo
              />
            </a>
          </div>

          <div className="hidden md:flex flex-grow justify-center space-x-6 lg:space-x-8 whitespace-nowrap overflow-hidden">
            <a href="#about-us" className="menu-item text-sm font-medium text-gray-700 hover:text-[#4B73B7] transition-colors duration-200">{t('aboutUs.title')}</a>

            <div
              className="relative"
              onMouseEnter={() => handleMouseEnterDesktop('ourServices')}
            >
              <button className={`menu-item text-sm font-medium text-gray-700 hover:text-[#4B73B7] transition-colors duration-200 flex items-center ${activeMenu === 'ourServices' ? 'text-[#4B73B7]' : ''}`}>
                {t('ourServices.title')} <ChevronDown className={`ml-1 w-4 h-4 transition-transform ${activeMenu === 'ourServices' ? 'rotate-180' : ''}`} />
              </button>
            </div>

            <a href="#sector-activity" className="menu-item text-sm font-medium text-gray-700 hover:text-[#4B73B7] transition-colors duration-200">{t('sectorActivity.title')}</a>
            <a href="#materials" className="menu-item text-sm font-medium text-gray-700 hover:text-[#4B73B7] transition-colors duration-200">{t('materials.title')}</a>
            <a href="#our-machines" className="menu-item text-sm font-medium text-gray-700 hover:text-[#4B73B7] transition-colors duration-200">{t('ourMachines.title')}</a>
            <a href="#partners" className="menu-item text-sm font-medium text-gray-700 hover:text-[#4B73B7] transition-colors duration-200">{t('partners.title')}</a>
            {/* <a href="#join-us" className="menu-item text-sm font-medium text-gray-700 hover:text-[#4B73B7] transition-colors duration-200">{t('joinUs.title')}</a> */}
          </div>

          <div className="hidden md:block whitespace-nowrap">
            <a
              href="#contact"
              className="px-6 py-3 rounded-full text-sm font-semibold text-white
                         bg-gradient-to-r from-[#88B04B] to-[#73953F]
                         hover:from-[#7a9e42] hover:to-[#6b8b3a]
                         transition-all duration-300 shadow-lg"
            >
              {t('contact_us.title')}
            </a>
          </div>

          <div className="md:hidden">
            <button id="mobile-menu-button" onClick={toggleMobileMenu} className="text-gray-800 p-2 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4B73B7]">
              {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {activeMenu === 'ourServices' && <MegaMenu activeMenu={activeMenu} setActiveMenu={setActiveMenu} t={t} />}

        {/* Menu mobile - Affiché uniquement si isMobileMenuOpen est true */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg pb-4 max-h-[calc(100vh-100px)] overflow-y-auto"> {/* Ajout max-h et overflow-y */}
            <div className="px-4 pt-2 pb-3 space-y-2">
              <a href="#about-us" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md">{t('aboutUs.title')}</a>

              <div className="relative">
                <button
                  onClick={() => setActiveMenu(activeMenu === 'ourServices' ? null : 'ourServices')}
                  className="w-full text-left py-2 px-3 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md flex justify-between items-center"
                >
                  {t('ourServices.title')} <ChevronDown className={`w-5 h-5 transition-transform ${activeMenu === 'ourServices' ? 'rotate-180' : ''}`} />
                </button>
                {activeMenu === 'ourServices' && (
                  <div className="pl-4 mt-2 space-y-1">
                    <a href="#service-conception" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs text-gray-600 hover:bg-gray-100 rounded-md">{t('ourServices.section1.title')}</a>
                    <a href="#service-moules" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs text-gray-600 hover:bg-gray-100 rounded-md">{t('ourServices.section2.title')}</a>
                    <a href="#service-injection" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs text-gray-600 hover:bg-gray-100 rounded-md">{t('ourServices.section3.title')}</a>
                    <a href="#service-contenants" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs text-gray-600 hover:bg-gray-100 rounded-md">{t('ourServices.section4.title')}</a>
                    <a href="#service-logistique" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs text-gray-600 hover:bg-gray-100 rounded-md">{t('ourServices.section5.title')}</a>
                    <a href="#service-client" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs text-gray-600 hover:bg-gray-100 rounded-md">{t('ourServices.section6.title')}</a>
                  </div>
                )}
              </div>

              <a href="#sector-activity" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md">{t('sectorActivity.title')}</a>
              <a href="#materials" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md">{t('materials.title')}</a>
              <a href="#our-machines" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md">{t('ourMachines.title')}</a>
              <a href="#partners" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md">{t('partners.title')}</a>
              <a href="#join-us" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 px-3 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded-md">{t('joinUs.title')}</a>

              <div className="pt-4 border-t border-gray-200 flex justify-around">
                <button
                  onClick={() => changeLanguage('fr')}
                  className={`py-2 px-4 rounded-md text-xs font-medium ${i18n.language === 'fr' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  FR
                </button>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`py-2 px-4 rounded-md text-xs font-medium ${i18n.language === 'en' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  EN
                </button>
                <button
                  onClick={() => changeLanguage('ar')}
                  className={`py-2 px-4 rounded-md text-xs font-medium ${i18n.language === 'ar' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  AR
                </button>
                <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="py-2 px-4 rounded-md text-xs font-medium text-white
                             bg-gradient-to-r from-[#88B04B] to-[#73953F]
                             hover:from-[#7a9e42] hover:to-[#6b8b3a]
                             transition-colors duration-200">
                  {t('contact_us.title')}
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

// --- Composant App (principal) ---
// --- Composant App (principal) ---
function App() {
  const { t, i18n } = useTranslation();
  const [isI18nReady, setIsI18nReady] = useState(false);

  // État pour le formulaire de contact
  const [formStatus, setFormStatus] = useState(''); // Peut être '', 'sending', 'success', 'error'

  // Fonction pour envoyer l'email
  const handleFormSubmit = async (e) => {
    e.preventDefault(); // Empêche la page de se recharger
    setFormStatus('sending');

    // Récupère les données du formulaire
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      // Envoi via FormSubmit en AJAX (sans quitter la page)
      const response = await fetch("https://formsubmit.co/ajax/preformeinjection@gmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setFormStatus('success');
        e.target.reset(); // Vide le formulaire
        setTimeout(() => setFormStatus(''), 5000); // Cache le message de succès après 5s
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };


  useEffect(() => {
    if (i18n.isInitialized) {
      setIsI18nReady(true);
    } else {
      i18n.on('initialized', () => {
        setIsI18nReady(true);
      });
    }
  }, []);

  // NOUVEAU : Détecter la langue et forcer le sens de lecture (RTL/LTR)
  useEffect(() => {
    const isArabic = i18n.language && i18n.language.startsWith('ar');
    document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language || 'fr';
  }, [i18n.language]);

  if (!isI18nReady) {
    return <div className="flex justify-center items-center min-h-screen text-gray-700">Chargement des traductions...</div>;
  }

  const isRTL = i18n.language && i18n.language.startsWith('ar');

  return (
    <div className="min-h-screen font-inter antialiased relative bg-[#eef0f2] text-[#1d1d1f]" dir={isRTL ? 'rtl' : 'ltr'}>
      <style>
        {`
        section[id] {
          scroll-margin-top: 100px;
        }
        /* Ajustements automatiques pour inverser les éléments en Arabe (RTL) */
        [dir="rtl"] .text-left { text-align: right !important; }
        [dir="rtl"] .ml-1 { margin-left: 0 !important; margin-right: 0.25rem !important; }
        [dir="rtl"] .ml-2 { margin-left: 0 !important; margin-right: 0.5rem !important; }
        [dir="rtl"] .mr-3 { margin-right: 0 !important; margin-left: 0.75rem !important; }
        [dir="rtl"] .mr-4 { margin-right: 0 !important; margin-left: 1rem !important; }
        `}
      </style>
      <Header />

      {/* Hero Section */}
      {/* La section Hero n'a pas besoin de scroll-margin-top car elle est au début de la page et le header est au-dessus */}
      <div className="relative h-[80vh] flex items-center justify-center pt-[6rem]">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full overflow-hidden z-0">
          <video
            src="/videos/preform-pet.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            Votre navigateur ne supporte pas la balise vidéo.
          </video>
          {/* Overlay sombre translucide */}
          <div className="absolute inset-0 bg-black opacity-40"></div>
        </div>
        {/* Welcome Content */}
        <div className="relative z-10 text-center p-8 text-white"> {/* Texte blanc pour contraste */}
          {/* Réduction de la taille de la police pour les titres et paragraphes sur mobile */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">{t('homepage.hero.title')}</h1>
          {/* Réduit de 3 niveaux: text-xl -> text-sm, sm:text-2xl -> sm:text-base, md:text-3xl -> md:text-lg */}
          <h2 className="text-sm sm:text-base md:text-lg font-semibold mb-6 drop-shadow-lg">{t('homepage.hero.subtitle')}</h2>
          {/* Réduit de 3 niveaux: text-sm -> text-xs, sm:text-base -> sm:text-xs, md:text-lg -> md:text-sm */}
          <p className="text-xs sm:text-xs md:text-sm mb-2 max-w-2xl mx-auto drop-shadow-md" dangerouslySetInnerHTML={{ __html: t('homepage.hero.description') }}></p>
          {/* Réduit de 3 niveaux: text-base -> text-xs, sm:text-lg -> sm:text-sm, md:text-xl -> md:text-base */}
          <p className="text-xs sm:text-sm md:text-base font-bold mt-4 mb-8 drop-shadow-lg text-yellow-300" dangerouslySetInnerHTML={{ __html: t('homepage.hero.tagline') }}></p>
          <div className="flex justify-center space-x-4">
            <a
              href="#contact"
              className="px-8 py-4 rounded-full text-sm sm:text-base md:text-lg font-semibold text-white
                         bg-gradient-to-r from-[#88B04B] to-[#73953F]
                         hover:from-[#7a9e42] hover:to-[#6b8b3a]
                         transition-all duration-300 shadow-lg"
            >
              {t('homepage.hero.buttonQuote')}
            </a>
            <a
              href="#about-us"
              className="px-8 py-4 rounded-full text-sm sm:text-base md:text-lg font-semibold text-gray-800
                         bg-white hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              {t('homepage.hero.buttonLearnMore')}
            </a>
          </div>
        </div>
      </div>

      <main className="relative z-10 bg-gray-100">
        {/* Section Notre mission */}
        {/* <section id="our-mission" className="max-w-6xl mx-auto py-16 bg-white text-center shadow-[0_8px_24px_rgba(0,0,0,0.06)] border border-[#ece8df] rounded-[24px] px-8">
          <h3 className="text-3xl font-bold text-gray-800 mb-4">{t('homepage.mission.slogan')}</h3>
          <p className="text-lg text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('homepage.mission.paragraph1') }}></p>
          <p className="text-lg text-gray-700" dangerouslySetInnerHTML={{ __html: t('homepage.mission.paragraph2') }}></p>
        </section> */}

        {/* Section Ce que nous faisons */}
        {/* <section id="what-we-do" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
          <h3 className="text-3xl font-bold text-gray-800 mb-12">{t('homepage.whatWeDo.title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center">
              <Settings className="w-12 h-12 text-[#4B73B7] mb-4" />
              <h4 className="text-xl font-semibold text-gray-800 mb-2">{t('homepage.whatWeDo.block1.title')}</h4>
              <p className="text-gray-600">{t('homepage.whatWeDo.block1.description')}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center">
              <img src="https://placehold.co/48x48/4B73B7/ffffff?text=🧴" alt="Image of Containers" className="w-12 h-12 mb-4" /> {/* Image of Containers */}
              {/* <h4 className="text-xl font-semibold text-gray-800 mb-2">{t('homepage.whatWeDo.block2.title')}</h4>
              <p className="text-gray-600">{t('homepage.whatWeDo.block2.description')}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md flex flex-col items-center">
              <CheckCircle className="w-12 h-12 text-[#4B73B7] mb-4" />
              <h4 className="text-xl font-semibold text-gray-800 mb-2">{t('homepage.whatWeDo.block3.title')}</h4>
              <p className="text-gray-600">{t('homepage.whatWeDo.block3.description')}</p>
            </div>
          </div>
        </section> */} 

        {/* Section Pourquoi nous choisir */}
        <section id="why-choose-us" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
          <h3 className="text-3xl font-bold text-gray-800 mb-12">{t('homepage.whyChooseUs.title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="flex flex-col items-center p-4">
              <CheckCircle className="w-10 h-10 text-green-600 mb-3" />
              <p className="text-gray-700 font-medium">{t('homepage.whyChooseUs.block1.title')}</p>
            </div>
            <div className="flex flex-col items-center p-4">
              <Settings className="w-10 h-10 text-blue-600 mb-3" />
              <p className="text-gray-700 font-medium">{t('homepage.whyChooseUs.block2.title')}</p>
            </div>
            <div className="flex flex-col items-center p-4">
              <Leaf className="w-10 h-10 text-green-500 mb-3" />
              <p className="text-gray-700 font-medium">{t('homepage.whyChooseUs.block3.title')}</p>
            </div>
            <div className="flex flex-col items-center p-4">
              <Package className="w-10 h-10 text-purple-600 mb-3" />
              <p className="text-gray-700 font-medium">{t('homepage.whyChooseUs.block4.title')}</p>
            </div>
          </div>
        </section>

        {/* Section Appel à l’action final */}
        {/* <section id="final-cta" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
          <h3 className="text-4xl font-bold mb-4">{t('homepage.finalCta.title')}</h3>
          <p className="text-xl mb-4">{t('homepage.finalCta.paragraph1')}</p>
          <p className="text-lg mb-8">{t('homepage.finalCta.paragraph2')}</p>
          <a
            href="#contact"
            className="px-8 py-4 rounded-full text-lg font-semibold text-[#73953F]
                       bg-white hover:bg-gray-100 transition-all duration-300 shadow-lg"
            >
              {t('homepage.finalCta.button')}
            </a>
          </section> */}

          {/* Section About Us */}
          <section id="about-us" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">{t('aboutUs.title')}</h3>
            <p className="text-xl font-bold text-[#4B73B7] mb-4" dangerouslySetInnerHTML={{ __html: t('aboutUs.tagline') }}></p>
            <p className="text-lg text-gray-700 mb-8" dangerouslySetInnerHTML={{ __html: t('aboutUs.taglineDescription') }}></p>
            <p className="text-gray-700 mb-8 text-left" dangerouslySetInnerHTML={{ __html: t('aboutUs.mainParagraph') }}></p>
            
            <h4 className="text-2xl font-bold text-gray-800 mb-8">{t('aboutUs.commitmentsTitle')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
              {/* Commitment 1 */}
              <div className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm">
                <CheckCircle className="w-8 h-8 text-green-600 mr-4 flex-shrink-0" />
                <div className="text-left">
                  <h5 className="text-lg font-semibold text-gray-800">{t('aboutUs.commitment1.title')}</h5>
                  <p className="text-gray-600 text-sm">{t('aboutUs.commitment1.description')}</p>
                </div>
              </div>
              {/* Commitment 2 */}
              <div className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm">
                <Settings className="w-8 h-8 text-blue-600 mr-4 flex-shrink-0" />
                <div className="text-left">
                  <h5 className="text-lg font-semibold text-gray-800">{t('aboutUs.commitment2.title')}</h5>
                  <p className="text-gray-600 text-sm">{t('aboutUs.commitment2.description')}</p>
                </div>
              </div>
              {/* Commitment 3 */}
              <div className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm">
                <Leaf className="w-8 h-8 text-green-500 mr-4 flex-shrink-0" />
                <div className="text-left">
                  <h5 className="text-lg font-semibold text-gray-800">{t('aboutUs.commitment3.title')}</h5>
                  <p className="text-gray-600 text-sm">{t('aboutUs.commitment3.description')}</p>
                </div>
              </div>
              {/* Commitment 4 */}
              <div className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm">
                <Package className="w-8 h-8 text-purple-600 mr-4 flex-shrink-0" />
                <div className="text-left">
                  <h5 className="text-lg font-semibold text-gray-800">{t('aboutUs.commitment4.title')}</h5>
                  <p className="text-gray-600 text-sm">{t('aboutUs.commitment4.description')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section Nos Services */}
          <section id="our-services-section" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
            <h3 className="text-3xl font-bold text-gray-800 mb-8">{t('ourServices.title')}</h3>
            
            {/* Section 1: Conception & Étude Technique */}
            <div id="service-conception" className="w-full bg-gray-50 p-6 rounded-lg shadow-sm mb-8 text-left">
              <h4 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                <HardHat className="w-7 h-7 text-[#4B73B7] mr-3" /> {t('ourServices.section1.title')}
              </h4>
              <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('ourServices.section1.description') }}></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                {(Array.isArray(t('ourServices.section1.bullets', { returnObjects: true })) ? t('ourServices.section1.bullets', { returnObjects: true }) : []).map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* Section 2: Fabrication de moules */}
            <div id="service-moules" className="w-full bg-gray-50 p-6 rounded-lg shadow-sm mb-8 text-left">
              <h4 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                <Settings className="w-7 h-7 text-[#4B73B7] mr-3" /> {t('ourServices.section2.title')}
              </h4>
              <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('ourServices.section2.description') }}></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                {(Array.isArray(t('ourServices.section2.bullets', { returnObjects: true })) ? t('ourServices.section2.bullets', { returnObjects: true }) : []).map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* Section 3: Injection plastique */}
            <div id="service-injection" className="w-full bg-gray-50 p-6 rounded-lg shadow-sm mb-8 text-left">
              <h4 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                <Package className="w-7 h-7 text-[#4B73B7] mr-3" /> {t('ourServices.section3.title')}
              </h4>
              <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('ourServices.section3.description') }}></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                {(Array.isArray(t('ourServices.section3.bullets', { returnObjects: true })) ? t('ourServices.section3.bullets', { returnObjects: true }) : []).map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* Section 4: Préformes, Flacons & Contenants */}
            <div id="service-contenants" className="w-full bg-gray-50 p-6 rounded-lg shadow-sm mb-8 text-left">
              <h4 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                {/* <img src="https://placehold.co/28x28/4B73B7/ffffff?text=🧴" alt="Image of Flacon Icon" className="w-7 h-7 mr-3" /> {t('ourServices.section4.title')} */}
                <img 
                    src="/images/icone_tube.png" 
                    alt="Flacon Icon" 
                    className="w-7 h-7 mr-3 object-contain" 
                  />
              </h4>
              <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('ourServices.section4.description') }}></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                {(Array.isArray(t('ourServices.section4.bullets', { returnObjects: true })) ? t('ourServices.section4.bullets', { returnObjects: true }) : []).map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* Section 5: Logistique & Livraison */}
            <div id="service-logistique" className="w-full bg-gray-50 p-6 rounded-lg shadow-sm mb-8 text-left">
              <h4 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                <Truck className="w-7 h-7 text-[#4B73B7] mr-3" /> {t('ourServices.section5.title')}
              </h4>
              <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('ourServices.section5.description') }}></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                {(Array.isArray(t('ourServices.section5.bullets', { returnObjects: true })) ? t('ourServices.section5.bullets', { returnObjects: true }) : []).map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* Section 6: Service client & suivi */}
            <div id="service-client" className="w-full bg-gray-50 p-6 rounded-lg shadow-sm mb-8 text-left">
              <h4 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                <Users className="w-7 h-7 text-[#4B73B7] mr-3" /> {t('ourServices.section6.title')}
              </h4>
              <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: t('ourServices.section6.description') }}></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                {(Array.isArray(t('ourServices.section6.bullets', { returnObjects: true })) ? t('ourServices.section6.bullets', { returnObjects: true }) : []).map((bullet, index) => (
                  <li key={index}>{bullet}</li>
                ))}
              </ul>
            </div>

            {/* CTA final pour les services */}
            <div className="w-full bg-gradient-to-r from-[#88B04B] to-[#73953F] text-white p-8 rounded-lg shadow-md text-center">
              <h4 className="text-2xl font-bold mb-4">{t('ourServices.cta.question')}</h4>
              <p className="text-lg mb-6">{t('ourServices.cta.action')}</p>
              <a
                href="#contact"
                className="px-8 py-4 rounded-full text-lg font-semibold text-[#73953F]
                           bg-white hover:bg-gray-100 transition-all duration-300 shadow-lg"
              >
                {t('contact_us.title')}
              </a>
            </div>
          </section>

          {/* Section Secteur d'activité */}
          <section id="sector-activity" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
            <h3 className="text-3xl font-bold text-gray-800 mb-8">{t('sectorActivity.title')}</h3>
            <p className="text-lg text-gray-700 mb-8" dangerouslySetInnerHTML={{ __html: t('sectorActivity.mainParagraph') }}></p>
            
            <h4 className="text-2xl font-bold text-gray-800 mb-6">{t('sectorActivity.sectorsSubtitle')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
              {/* Secteur Pharmaceutique */}
              <div className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm">
                <Pill className="w-8 h-8 text-blue-500 mr-4 flex-shrink-0" />
                <div className="text-left">
                  <h5 className="text-lg font-semibold text-gray-800">{t('sectorActivity.pharmaceutical.title')}</h5>
                  <p className="text-gray-600 text-sm">{t('sectorActivity.pharmaceutical.description')}</p>
                </div>
              </div>
              {/* Secteur Cosmétique & Hygiène */}
              <div className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm">
                <Sparkles className="w-8 h-8 text-pink-500 mr-4 flex-shrink-0" />
                <div className="text-left">
                  <h5 className="text-lg font-semibold text-gray-800">{t('sectorActivity.cosmetics.title')}</h5>
                  <p className="text-gray-600 text-sm">{t('sectorActivity.cosmetics.description')}</p>
                </div>
              </div>
              {/* Secteur Chimie & Produits ménagers */}
              <div className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm">
                <FlaskConical className="w-8 h-8 text-purple-500 mr-4 flex-shrink-0" />
                <div className="text-left">
                  <h5 className="text-lg font-semibold text-gray-800">{t('sectorActivity.chemical.title')}</h5>
                  <p className="text-gray-600 text-sm">{t('sectorActivity.chemical.description')}</p>
                </div>
              </div>
              {/* Secteur Agroalimentaire */}
              <div className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm">
                <UtensilsCrossed className="w-8 h-8 text-orange-500 mr-4 flex-shrink-0" />
                <div className="text-left">
                  <h5 className="text-lg font-semibold text-gray-800">{t('sectorActivity.food.title')}</h5>
                  <p className="text-gray-600 text-sm">{t('sectorActivity.food.description')}</p>
                </div>
              </div>
            </div>

            {/* <h4 className="text-2xl font-bold text-gray-800 mt-12 mb-6">{t('sectorActivity.whyWorkWithUsTitle')}</h4>
            <ul className="list-disc list-inside text-gray-700 text-left w-full max-w-md mx-auto space-y-2">
              {(Array.isArray(t('sectorActivity.whyWorkWithUsBullets', { returnObjects: true })) ? t('sectorActivity.whyWorkWithUsBullets', { returnObjects: true }) : []).map((bullet, index) => (
                  <li key={index}>{bullet}</li>
              ))}
            </ul> */}
          </section>

          {/* Section Matières utilisées */}
          <section id="materials" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
            {/* <h3 className="text-3xl font-bold text-gray-800 mb-8 flex items-center">
              <img src="https://placehold.co/32x32/4B73B7/ffffff?text=🧪" alt="Image of Flask Icon" className="w-8 h-8 mr-3" /> 
              {t('materials.title')}

            </h3> */}
            <h3 className="text-3xl font-bold text-gray-800 mb-8">{t('materials.title')}</h3>
            <p className="text-lg text-gray-700 mb-8" dangerouslySetInnerHTML={{ __html: t('materials.mainParagraph') }}></p>
            
            <h4 className="text-2xl font-bold text-gray-800 mb-6">{t('materials.mainMaterialsSubtitle')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
              {(Array.isArray(t('materials.mainMaterials', { returnObjects: true })) ? t('materials.mainMaterials', { returnObjects: true }) : []).map((material, index) => (
                <div key={index} className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm text-left">
                  <div className="text-left">
                    <h5 className="text-lg font-semibold text-gray-800">{material.title}</h5>
                    <p className="text-gray-600 text-sm">{material.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <h4 className="text-2xl font-bold text-gray-800 mt-12 mb-6">{t('materials.otherMaterialsSubtitle')}</h4>
            <p className="text-lg text-gray-700 mb-4">{t('materials.otherMaterialsDescription')}</p>
            <p className="text-lg text-gray-700">{t('materials.otherMaterialsCallToAction')}</p>
          </section>

          {/* Section Nos Machines */}
          <section id="our-machines" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
            <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">{t('ourMachines.title')}</h3>

            {/* Machine 1 : Injection */}
            <div className="mb-12 bg-gray-50 p-8 rounded-xl shadow-sm flex flex-col gap-8">
              
              {/* Photo de la machine d'injection en haut */}
              <div className="w-full h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden shadow-md">
                <img src="/images/inj_machine.png" alt="Machine d'injection plastique" className="w-full h-full object-cover" />
              </div>

              {/* Texte en bas */}
              <div>
                <h4 className="text-2xl font-bold text-gray-800 mb-4">{t('ourMachines.injectionPlastique.title')}</h4>
                <p className="text-xl font-semibold text-[#4B73B7] mb-6">{t('ourMachines.injectionPlastique.machineName')}</p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                  {(Array.isArray(t('ourMachines.injectionPlastique.specs', { returnObjects: true })) ? t('ourMachines.injectionPlastique.specs', { returnObjects: true }) : []).map((spec, index) => (
                    <li key={index}>{spec}</li>
                  ))}
                </ul>
                
                <div className="bg-white p-6 rounded-lg border border-gray-200 mt-6">
                  <h5 className="text-lg font-bold text-gray-800 mb-4">Moules associés :</h5>
                  {(Array.isArray(t('ourMachines.injectionPlastique.molds', { returnObjects: true })) ? t('ourMachines.injectionPlastique.molds', { returnObjects: true }) : []).map((mold, index) => (
                    <div key={index} className="mb-4 last:mb-0">
                      <p className="font-semibold text-gray-800">{mold.name}</p>
                      <ul className="list-disc list-inside text-gray-600 ml-4 space-y-1">
                        {(Array.isArray(mold.details) ? mold.details : []).map((detail, detailIndex) => (
                          <li key={detailIndex}>{detail}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Machine 2 : Soufflage */}
            <div className="mb-8 bg-gray-50 p-8 rounded-xl shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Photo de la machine de soufflage (placée à gauche sur grand écran pour alterner) */}
                <div className="w-full h-full min-h-[300px] rounded-xl overflow-hidden shadow-md lg:order-1 order-2">
                  <img src="/images/blowing_machine.png" alt="Machine de soufflage" className="w-full h-full object-cover" />
                </div>

                <div className="lg:order-2 order-1">
                  <h4 className="text-2xl font-bold text-gray-800 mb-4">{t('ourMachines.soufflageBouteilles.title')}</h4>
                  <p className="text-xl font-semibold text-[#4B73B7] mb-6">{t('ourMachines.soufflageBouteilles.machineName')}</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                    {(Array.isArray(t('ourMachines.soufflageBouteilles.specs', { returnObjects: true })) ? t('ourMachines.soufflageBouteilles.specs', { returnObjects: true }) : []).map((spec, index) => (
                      <li key={index}>{spec}</li>
                    ))}
                  </ul>
                  
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h5 className="text-lg font-bold text-gray-800 mb-2">Moule associé :</h5>
                    <ul className="list-disc list-inside text-gray-600 ml-4 space-y-1">
                      {(Array.isArray(t('ourMachines.soufflageBouteilles.molds', { returnObjects: true })) ? t('ourMachines.soufflageBouteilles.molds', { returnObjects: true }) : []).map((mold, index) => (
                        <li key={index}>{mold}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section Partners */}
          <section id="partners" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
            <h3 className="text-3xl font-bold text-gray-800 mb-8">{t('partners.title')}</h3>
            <p className="text-lg text-gray-700 mb-12" dangerouslySetInnerHTML={{ __html: t('partners.intro') }}></p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full mb-12">
              {/* Partner 1: IPC */}
              <div className="flex flex-col items-center p-6 rounded-lg shadow-sm bg-gray-50 hover:shadow-lg hover:scale-105 transition-all duration-300">
                <a href={t('partners.partner1.linkUrl')} target="_blank" rel="noopener noreferrer" className="mb-4">
                  <img src={t('partners.partner1.logoUrl')} alt={t('partners.partner1.name')} className="h-24 w-auto object-contain rounded-lg" />
                </a>
                <h4 className="text-xl font-semibold text-gray-800 mb-2">{t('partners.partner1.name')}</h4>
                <p className="text-gray-600 text-sm">{t('partners.partner1.description')}</p>
              </div>

              {/* Partner 2: SONLY */}
              <div className="flex flex-col items-center p-6 rounded-lg shadow-sm bg-white hover:shadow-lg hover:scale-105 transition-all duration-300">
                <a href={t('partners.partner2.linkUrl')} target="_blank" rel="noopener noreferrer" className="mb-4">
                  <img src={t('partners.partner2.logoUrl')} alt={t('partners.partner2.name')} className="h-24 w-auto object-contain rounded-lg" />
                </a>
                <h4 className="text-xl font-semibold text-gray-800 mb-2">{t('partners.partner2.name')}</h4>
                <p className="text-gray-600 text-sm">{t('partners.partner2.description')}</p>
              </div>

              {/* Partner 3: Haijiang */}
              <div className="flex flex-col items-center p-6 rounded-lg shadow-sm bg-gray-50 hover:shadow-lg hover:scale-105 transition-all duration-300">
                <a href={t('partners.partner3.linkUrl')} target="_blank" rel="noopener noreferrer" className="mb-4">
                  <img src={t('partners.partner3.logoUrl')} alt={t('partners.partner3.name')} className="h-24 w-auto object-contain rounded-lg" />
                </a>
                <h4 className="text-xl font-semibold text-gray-800 mb-2">{t('partners.partner3.name')}</h4>
                <p className="text-gray-600 text-sm">{t('partners.partner3.description')}</p>
              </div>
            </div>

            {/* <h4 className="text-2xl font-bold text-gray-800 mt-8 mb-6">{t('partners.whyAsset.title')}</h4>
            <ul className="list-disc list-inside text-gray-700 text-left w-full max-w-md mx-auto space-y-2">
              {(Array.isArray(t('partners.whyAsset.bullets', { returnObjects: true })) ? t('partners.whyAsset.bullets', { returnObjects: true }) : []).map((bullet, index) => (
                <li key={index}>{bullet}</li>
              ))}
            </ul> */}
          </section>

          {/* Section Join Us */}
          {/* <section id="join-us" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
            <h3 className="text-3xl font-bold text-gray-800 mb-8">{t('joinUs.title')}</h3>
            <p className="text-lg text-gray-700 mb-8" dangerouslySetInnerHTML={{ __html: t('joinUs.intro') }}></p>
            
            <h4 className="text-2xl font-bold text-gray-800 mb-6">{t('joinUs.openPositionsTitle')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full mb-12">
              {(Array.isArray(t('joinUs.positions', { returnObjects: true })) ? t('joinUs.positions', { returnObjects: true }) : []).map((position, index) => (
                <div key={index} className="flex items-start p-4 bg-gray-50 rounded-lg shadow-sm text-left">
                  <div className="text-left">
                    <h5 className="text-lg font-semibold text-gray-800">{position.title}</h5>
                    <p className="text-gray-600 text-sm">{position.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="w-full bg-gradient-to-r from-[#88B04B] to-[#73953F] text-white p-8 rounded-lg shadow-md text-center">
              <h4 className="text-2xl font-bold mb-4">{t('joinUs.cta.title')}</h4>
              <p className="text-lg mb-6">
                Envoyez votre CV à <a href={`mailto:${t('joinUs.cta.email')}`} className="underline font-semibold text-white hover:text-gray-100">{t('joinUs.cta.email')}</a>
              </p>
            </div>
          </section> */}

          <section id="contact" className="py-16 bg-white text-center shadow-md mx-auto max-w-7xl rounded-lg my-12 px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h3 className="text-3xl font-bold text-gray-800 mb-8 text-left">{t('contact_us.title')}</h3>
              <form className="space-y-6" onSubmit={handleFormSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact_us.form.name')}</label>
                    <input type="text" name="Nom" required className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#4B73B7] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact_us.form.company')}</label>
                    <input type="text" name="Entreprise" className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#4B73B7] focus:border-transparent" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact_us.form.phone')}</label>
                    <input type="tel" name="Téléphone" required className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#4B73B7] focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact_us.form.email')}</label>
                    <input type="email" name="Email" required className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#4B73B7] focus:border-transparent" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact_us.form.message')}</label>
                  <textarea rows="5" name="Message" required className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#4B73B7] focus:border-transparent"></textarea>
                </div>

                {/* Champs cachés utiles pour FormSubmit (Anti-spam et désactivation du captcha) */}
                <input type="text" name="_honey" style={{ display: 'none' }} />
                <input type="hidden" name="_captcha" value="false" />

                <div className="flex items-center space-x-4">
                  <button 
                    type="submit" 
                    disabled={formStatus === 'sending'}
                    className={`bg-[#e24a29] hover:bg-[#c93d1f] text-white font-bold py-2 px-6 rounded-md flex items-center transition-colors ${formStatus === 'sending' ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {formStatus === 'sending' ? 'Envoi...' : t('contact_us.form.send')}
                  </button>

                  {/* Messages de retour */}
                  {formStatus === 'success' && (
                    <span className="text-green-600 text-sm font-medium flex items-center animate-fade-in-out">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Message envoyé avec succès !
                    </span>
                  )}
                  {formStatus === 'error' && (
                    <span className="text-red-600 text-sm font-medium animate-fade-in-out">
                      Une erreur est survenue. Veuillez réessayer.
                    </span>
                  )}
                </div>
              </form>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm flex flex-col h-full">
                <div className="p-6 bg-white flex-shrink-0 text-left">
                  <h4 className="text-xl font-bold text-gray-800 mb-4">{t('contact_us.factoryAddressTitle') || 'Factory Address'}</h4>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <MapPin className="w-5 h-5 text-gray-500 mr-3 mt-1 flex-shrink-0" />
                      <p className="text-gray-700">Ouled mohammed zone 7 02000 Chlef, Algérie</p>
                    </div>
                    <div className="flex items-center" >
                      <Phone className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                      <p className="text-gray-700">{t('contact_us.phone')}</p>
                    </div>
                    <div className="flex items-center">
                      <Mail className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
                      <a href={`mailto:${t('contact_us.email')}`} className="text-gray-700 hover:text-[#4B73B7]">{t('contact_us.email')}</a>
                    </div>
                  </div>
                </div>
                
                <div className="w-full flex-grow min-h-[300px] bg-gray-100">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d102941.51336965155!2d1.246412613524672!3d36.16616058913988!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12847c94efb794d9%3A0xf6c80261cb4062a4!2sChlef!5e0!3m2!1sfr!2sdz!4v1712882800000!5m2!1sfr!2sdz" 
                    width="100%" 
                    height="100%" 
                    style={{ border: 0 }} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Carte de Chlef"
                  ></iframe>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer (exemple) */}
<footer className="w-full mt-24 bg-[#21486c] rounded-[24px] overflow-hidden text-white relative z-10 shadow-[0_15px_40px_rgba(0,0,0,0.1)] mb-8">
          <div className="max-w-7xl mx-auto px-8 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
              
              {/* Colonne 1 : Logo et description */}
              <div className="space-y-6">
                <img src="/images/LOGO-INJECT-BLEU_fonds_transparent.png" alt="Preform Inject Logo" className="h-16 w-auto bg-white p-2 rounded-xl" />
                <p className="text-white/80 text-sm leading-relaxed">
                  {t('homepage.mission.paragraph1') ? t('homepage.mission.paragraph1').substring(0, 120) + '...' : 'Expert en injection plastique de précision et soufflage de corps creux pour les professionnels.'}
                </p>
                <div className="flex space-x-4 pt-2">
                  <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#9cc7a8] transition-colors">
                    <Linkedin className="w-5 h-5 text-white" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#9cc7a8] transition-colors">
                    <Facebook className="w-5 h-5 text-white" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#9cc7a8] transition-colors">
                    <Instagram className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>

              {/* Colonne 2 : Liens rapides */}
              <div>
                <h4 className="text-lg font-bold mb-6 text-white">{t('ourServices.title') || 'Nos Services'}</h4>
                <ul className="space-y-3">
                  <li><a href="#service-conception" className="text-white/80 hover:text-white hover:underline text-sm transition-colors">{t('ourServices.section1.title')}</a></li>
                  <li><a href="#service-moules" className="text-white/80 hover:text-white hover:underline text-sm transition-colors">{t('ourServices.section2.title')}</a></li>
                  <li><a href="#service-injection" className="text-white/80 hover:text-white hover:underline text-sm transition-colors">{t('ourServices.section3.title')}</a></li>
                  <li><a href="#service-contenants" className="text-white/80 hover:text-white hover:underline text-sm transition-colors">{t('ourServices.section4.title')}</a></li>
                </ul>
              </div>

              {/* Colonne 3 : Navigation */}
              <div>
                <h4 className="text-lg font-bold mb-6 text-white">Navigation</h4>
                <ul className="space-y-3">
                  <li><a href="#about-us" className="text-white/80 hover:text-white hover:underline text-sm transition-colors">{t('aboutUs.title')}</a></li>
                  <li><a href="#sector-activity" className="text-white/80 hover:text-white hover:underline text-sm transition-colors">{t('sectorActivity.title')}</a></li>
                  <li><a href="#materials" className="text-white/80 hover:text-white hover:underline text-sm transition-colors">{t('materials.title')}</a></li>
                  <li><a href="#our-machines" className="text-white/80 hover:text-white hover:underline text-sm transition-colors">{t('ourMachines.title')}</a></li>
                </ul>
              </div>

              {/* Colonne 4 : Contact */}
              <div>
                <h4 className="text-lg font-bold mb-6 text-white">{t('contact_us.title') || 'Contact'}</h4>
                <ul className="space-y-4">
                  <li className="flex items-start text-white/80 text-sm">
                    <MapPin className="w-5 h-5 mr-3 text-[#9cc7a8] flex-shrink-0" />
                    <span>Ouled mohammed zone 7 02000 Chlef, Algérie</span>
                  </li>
                  <li className="flex items-center text-white/80 text-sm" >
                    <Phone className="w-5 h-5 mr-3 text-[#9cc7a8] flex-shrink-0" />
                    <a href={`tel:${t('contact_us.phone')}`} className="hover:text-white">{t('contact_us.phone')}</a>
                  </li>
                  <li className="flex items-center text-white/80 text-sm">
                    <Mail className="w-5 h-5 mr-3 text-[#9cc7a8] flex-shrink-0" />
                    <a href={`mailto:${t('contact_us.email')}`} className="hover:text-white">{t('contact_us.email')}</a>
                  </li>
                </ul>
              </div>

            </div>

            <div className="border-t border-white/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-white/60 text-sm">
                © {new Date().getFullYear()} Preform Inject. Tous droits réservés.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0 text-sm text-white/60">
                <a href="#" className="hover:text-white transition-colors">Mentions légales</a>
                <a href="#" className="hover:text-white transition-colors">Politique de confidentialité</a>
              </div>
            </div>
          </div>
        </footer>

      </div>
   
  );
}

export default App;
